import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../context/LanguageContext';
import { normalizeSupportedLanguage } from '../../utils/createScopedI18n';
import { sacI18n } from './sacI18n';
import { analyzeSacTranscripts } from '../../services/marketingReportService';
import SacTranscriptsFilterModal from './SacTranscriptsFilterModal';
import './marketingSacTranscripts.scss';

const formatDateTime = (value) => {
  if (!value) return '--';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleString();
};

const formatRange = (dateFrom, dateTo, t) => {
  if (dateFrom && dateTo) return `${dateFrom} - ${dateTo}`;
  if (dateFrom) return `${dateFrom} -`;
  if (dateTo) return `- ${dateTo}`;
  return t('sac_noRange');
};

const MarketingSacTranscripts = ({ DesignSystemComponents }) => {
  const { Card, Button, Table, Modal, Input, ComboBox } =
    DesignSystemComponents || {};
  const navigate = useNavigate();

  const { language } = useLanguage();
  const mkLanguage = normalizeSupportedLanguage(language);

  if (sacI18n.language !== mkLanguage) {
    sacI18n.changeLanguage(mkLanguage);
  }

  const { t } = useTranslation(undefined, { i18n: sacI18n });

  const [showModal, setShowModal] = useState(false);
  const [historyRows, setHistoryRows] = useState([]);
  const [historyError, setHistoryError] = useState('');
  const [lastFilters, setLastFilters] = useState({
    dateFrom: '',
    dateTo: '',
    feeling: '',
    maxRows: 120,
  });

  const historyColumns = useMemo(
    () => [
      {
        accessor: 'createdAt',
        label: t('sac_col_date'),
        render: (row) => formatDateTime(row.createdAt),
      },
      { accessor: 'protocol', label: t('sac_col_protocol') },
      { accessor: 'dateRange', label: t('sac_col_range') },
      { accessor: 'feeling', label: t('sac_col_feeling') },
      { accessor: 'rows', label: t('sac_col_rows') },
      { accessor: 'status', label: t('sac_col_status') },
    ],
    [mkLanguage]
  );

  const handleAnalyze = async ({
    transcriptIds,
    steps,
    excludeOmittedAudio,
    protocol,
    filters,
  }) => {
    setHistoryError('');

    const response = await analyzeSacTranscripts({
      transcriptIds,
      steps,
      excludeOmittedAudio,
    });

    setLastFilters({
      dateFrom: filters?.dateFrom || '',
      dateTo: filters?.dateTo || '',
      feeling: filters?.feeling || '',
      maxRows: filters?.maxRows || 120,
    });

    setHistoryRows((prev) => [
      {
        id: `mk-sac-${Date.now()}`,
        createdAt: new Date().toISOString(),
        protocol: protocol?.label || '--',
        dateRange: formatRange(filters?.dateFrom, filters?.dateTo, t),
        feeling: filters?.feeling || t('sac_all'),
        rows: transcriptIds.length,
        status: t('sac_inProgress'),
      },
      ...prev,
    ]);

    navigate('/marketing/analisis-general', {
      state: {
        response,
        transcriptIds,
        stepsCount: steps?.length || 0,
        protocol,
        filters,
        requestedAt: new Date().toISOString(),
      },
    });
  };

  if (!Card || !Button || !Table) return null;

  return (
    <div className='marketing-sac-page'>
      <div className='mb-4 d-flex justify-content-between align-items-start flex-wrap gap-2'>
        <div>
          <h2 className='fw-semibold mb-1'>{t('sac_pageTitle')}</h2>
          <h4 className='fw-normal mb-0'>{t('sac_pageSubtitle')}</h4>
        </div>
        <Button
          label={t('sac_newAnalysis')}
          variant='primary'
          size='sm'
          onClick={() => setShowModal(true)}
        />
      </div>

      <Card specialPadding='px-4 pt-4 pb-3'>
        <div className='marketing-sac__meta d-flex flex-wrap gap-3 mb-3'>
          <span className='caption'>{t('sac_historyLabel')}</span>
          <span className='caption'>
            {t('sac_lastLimit', { value: lastFilters.maxRows || 120 })}
          </span>
          <span className='caption'>
            {t('sac_totalAnalysis', { count: historyRows.length })}
          </span>
        </div>

        {historyError && <p className='text-danger mb-3'>{historyError}</p>}

        <div className='table-container marketing-sac__history-table'>
          <Table columns={historyColumns} data={historyRows} />
        </div>

        {historyRows.length === 0 && (
          <div className='text-center p-3'>
            <p className='caption font-light mb-0'>{t('sac_empty')}</p>
          </div>
        )}
      </Card>

      <SacTranscriptsFilterModal
        Modal={Modal}
        Input={Input}
        ComboBox={ComboBox}
        Button={Button}
        Table={Table}
        show={showModal}
        onClose={() => setShowModal(false)}
        onAnalyze={handleAnalyze}
        initialFilters={lastFilters}
      />
    </div>
  );
};

export default MarketingSacTranscripts;
